import type { FormOptionV2, VendorOptionV2 } from "@credit-acceptance/step6-bff-client";

import { withOption } from "./withOption";

/** The form-number entry the BFF returns last; saved verbatim as `formNo` so `CV_TPAP_*` stips can match it. */
// eslint-disable-next-line i18n-text/no-en -- the BFF's literal form name, not display copy (the label is translated)
export const MY_FORM_NOT_LISTED = "My Form Not Listed";

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

/**
 * Vendor dropdown options keyed by vendor id, so a vendor change can send the id back on save. The product's own
 * vendor is appended when the BFF list omits it, keeping it selectable.
 * @param vendors `additionalProducts.options.<product>.vendors`.
 * @param current The product's saved vendor.
 * @param current.id The saved `vendorId`.
 * @param current.name The saved `vendorName`.
 * @returns {SelectOption[]} Options, `value` being the stringified vendor id.
 */
export function buildVendorOptions(
  vendors: readonly VendorOptionV2[] | undefined,
  current: { id?: number | null; name?: string | null }
): SelectOption[] {
  const options = (vendors ?? [])
    .filter((vendor): vendor is VendorOptionV2 & { id: number } => typeof vendor.id === "number")
    .map((vendor) => ({ value: String(vendor.id), label: vendor.name ?? String(vendor.id) }));
  const hasCurrent = typeof current.id === "number" && !options.some((option) => option.value === String(current.id));
  return hasCurrent ? [...options, { value: String(current.id), label: current.name ?? String(current.id) }] : options;
}

/**
 * Form-number dropdown options: "Select an Option", the vendor's forms (plus the saved one when absent), then the
 * BFF's "My Form Not Listed" entry when it returned one.
 * @param forms The forms route's list for the selected vendor.
 * @param savedFormNo The product's saved `formNo`.
 * @param labels Translated labels.
 * @param labels.selectAnOption Label for the empty-selection entry.
 * @param labels.formNotListed Label for the "My Form Not Listed" entry.
 * @param selectAnOptionValue Value of the empty-selection entry.
 * @returns {SelectOption[]} Options, `value` being the form number string.
 */
export function buildFormOptions(
  forms: readonly FormOptionV2[] | undefined,
  savedFormNo: string | null | undefined,
  labels: { selectAnOption: string; formNotListed: string },
  selectAnOptionValue: string
): SelectOption[] {
  const numbers = (forms ?? []).flatMap((form) => (form.formNumber ? [form.formNumber] : []));
  const listed = numbers.filter((number) => number !== MY_FORM_NOT_LISTED);
  const showNotListed = numbers.includes(MY_FORM_NOT_LISTED) || savedFormNo === MY_FORM_NOT_LISTED;
  return [
    { value: selectAnOptionValue, label: labels.selectAnOption },
    ...withOption(listed, savedFormNo === MY_FORM_NOT_LISTED ? undefined : savedFormNo).map((number) => ({
      value: number,
      label: number,
    })),
    ...(showNotListed ? [{ value: MY_FORM_NOT_LISTED, label: labels.formNotListed }] : []),
  ];
}
